import * as React from 'react';
import { HttpErrorModel } from '../models/http-error';
import { objectToMap } from '../utils/object/object-to-map';
import { HookBackendConfigInterface, useBackend } from './backend';
import { RequestParams } from './http';

export interface PaginationConfig<Data> {
  endpoint?: string;
  params?: RequestParams;
  configBackend?: HookBackendConfigInterface;
  inMemory?: {
    data: Data[];
    loadingDelay?: number;
  };
}

export interface PaginationPage {
  index?: number;
  size?: number;
}

export interface PaginationSort {
  field?: string;
  type?: 'ASC' | 'DESC';
}

export interface PaginationRequest<Filter> {
  filter?: Filter;
  page?: PaginationPage;
  sort?: PaginationSort;
}

export interface PaginationResponse<Data> {
  total: number;
  items: Data[];
}

export interface HookPaginationInterface<Data, Filter> {
  data: PaginationResponse<Data>;
  loading: boolean;
  error?: HttpErrorModel;
  fetchData(request?: PaginationRequest<Filter>): Promise<void>;
}

export function usePagination<Data, Filter>(
  endpointOrConfig: string | PaginationConfig<Data>,
): HookPaginationInterface<Data, Filter> {
  const [endpoint, params, configBackend, inMemoryData, inMemoryLoadingDelay] =
    React.useMemo(() => {
      let _endpoint = '';
      let _params: RequestParams = new Map();
      let _configBackend: HookBackendConfigInterface = {};
      let _inMemoryData: Data[] | undefined = undefined;
      let _inMemoryLoadingDelay = 0;

      if (typeof endpointOrConfig === 'string') {
        _endpoint = endpointOrConfig;
      } else {
        if (endpointOrConfig.endpoint) {
          _endpoint = endpointOrConfig.endpoint;
        }
        if (endpointOrConfig.params) {
          _params = endpointOrConfig.params;
        }
        if (endpointOrConfig.configBackend) {
          _configBackend = endpointOrConfig.configBackend;
        }
        if (endpointOrConfig.inMemory) {
          _inMemoryData = endpointOrConfig.inMemory.data;
          _inMemoryLoadingDelay = endpointOrConfig.inMemory?.loadingDelay ?? 0;
        }
      }

      return [
        _endpoint,
        _params,
        _configBackend,
        _inMemoryData,
        _inMemoryLoadingDelay,
      ];
    }, [endpointOrConfig]);

  const emptyData = React.useMemo(
    () => ({
      total: 0,
      items: [] as Data[],
    }),
    [],
  );

  const [data, setData] = React.useState<PaginationResponse<Data>>(emptyData);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<HttpErrorModel>();

  const { backendGet } = useBackend(configBackend);

  const fetchData = React.useCallback(
    ({ filter, page = {}, sort = {} }: PaginationRequest<Filter> = {}) => {
      const pageIndex = page?.index ?? 0;
      const pageSize = page?.size ?? 10;
      const sortType = sort?.type ?? 'ASC';
      const sortField = sort?.field as string;

      setLoading(true);
      setData(emptyData);
      setError(undefined);

      if (Array.isArray(inMemoryData)) {
        return new Promise<void>((resolve, reject) => {
          const start = pageIndex * pageSize;
          const end = pageSize * (pageIndex + 1);
          const items = inMemoryData.slice(start, end);
          // TODO Tratar a ordenação inMemory
          // TODO Tratar o filtro inMemory
          setTimeout(() => {
            if (start < 0 || start >= end) {
              setError(new HttpErrorModel('Índice não encontrado'));
              reject();
            } else {
              setData({
                items,
                total: inMemoryData.length,
              });
              resolve();
            }
            setLoading(false);
          }, inMemoryLoadingDelay);
        });
      }

      const paramsToRequest =
        params instanceof Map ? params : objectToMap(params);

      paramsToRequest.set('pageIndex', pageIndex);
      paramsToRequest.set('pageSize', pageSize);
      paramsToRequest.set('sortType', sortType);
      paramsToRequest.set('sortField', sortField);

      if (typeof filter === 'object' && filter !== null) {
        Object.entries(filter).forEach(([key, value]) =>
          paramsToRequest.set(key, value + ''),
        );
      } else if (filter !== undefined) {
        paramsToRequest.set('filter', filter as unknown as string);
      }

      return new Promise<void>((resolve, reject) => {
        backendGet<PaginationResponse<Data>>(endpoint, paramsToRequest)
          .then((result) => {
            setData({
              total: result?.total ?? 0,
              items: result?.items ?? [],
            });
            resolve();
          })
          .catch((failure) => {
            // TODO Tratar se o erro não é desse tipo e traduzir para o mesmo
            setError(failure as HttpErrorModel);
            reject();
          })
          .finally(() => setLoading(false));
      });
    },
    [
      endpoint,
      params,
      emptyData,
      inMemoryData,
      inMemoryLoadingDelay,
      backendGet,
    ],
  );

  return {
    data,
    loading,
    error,
    fetchData,
  };
}
